import { useEffect, useState } from 'react';
import { eventsAPI, editionsAPI, tracksAPI, sessionsAPI, speakersAPI } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Eye, ExternalLink } from 'lucide-react';

import {
  initialFormData,
  generateSlug,
  BasicInfoTab,
  MediaTab,
  TicketsTab,
  ContentTab,
  LocationTab,
  SeoTab,
  AnalyticsTab,
  AgendaTab,
  BlocksTab,
} from '../../components/admin/events';

export const EventsAdminPage = () => {
  const [events, setEvents] = useState([]);
  const [editions, setEditions] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [topicInput, setTopicInput] = useState('');
  const [seoKeywordInput, setSeoKeywordInput] = useState('');
  const [eventAnalytics, setEventAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [eventsRes, editionsRes, tracksRes, speakersRes, sessionsRes] = await Promise.all([
        eventsAPI.getAll({ include_drafts: true }), editionsAPI.getAll(),
        tracksAPI.getAll(), speakersAPI.getAll(), sessionsAPI.getAll(),
      ]);
      setEvents(eventsRes.data); setEditions(editionsRes.data);
      setTracks(tracksRes.data); setSpeakers(speakersRes.data); setSessions(sessionsRes.data);
    } catch (err) { toast.error('Errore nel caricamento'); console.error(err); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setFormData(initialFormData); setEditingEvent(null); setTopicInput(''); };

  /* ---------- Sponsor groups migration helper ---------- */
  const buildSponsorGroupsFromLegacy = (sponsorsFlat) => {
    if (!Array.isArray(sponsorsFlat) || sponsorsFlat.length === 0) return [];
    const groupsByTier = {};
    const order = [];
    sponsorsFlat.forEach((s) => {
      const tier = s.tier || 'Partner';
      if (!groupsByTier[tier]) {
        groupsByTier[tier] = { id: `grp-${tier}-${Math.random().toString(36).slice(2, 9)}`, name: tier, items: [] };
        order.push(tier);
      }
      groupsByTier[tier].items.push({ name: s.name || '', logo: s.logo || '', url: s.url || '' });
    });
    return order.map((t) => groupsByTier[t]);
  };

  const flattenSponsorGroups = (groups) => {
    if (!Array.isArray(groups)) return [];
    const flat = [];
    groups.forEach((g) => {
      (g.items || []).forEach((it) => {
        flat.push({ name: it.name || '', logo: it.logo || '', url: it.url || '', tier: g.name || 'Partner' });
      });
    });
    return flat;
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    const existingGroups = Array.isArray(event.sponsor_groups) && event.sponsor_groups.length > 0
      ? event.sponsor_groups
      : buildSponsorGroupsFromLegacy(event.sponsors || []);
    setFormData({
      title: event.title, edition_id: event.edition_id, location: event.location,
      city: event.city || '', address: event.address || '', venue_name: event.venue_name || '',
      description: event.description || '', date: event.date || '', end_date: event.end_date || '',
      event_start_time: event.event_start_time || '', event_end_time: event.event_end_time || '',
      hero_image: event.hero_image || '', hero_image_mobile: event.hero_image_mobile || '', hero_video: event.hero_video || '',
      slug: event.slug, status: event.status, stats: event.stats || [],
      ticket_url: event.ticket_url || '', ticket_price: event.ticket_price || '',
      ticket_label: event.ticket_label || '',
      sponsors: event.sponsors || [],
      sponsor_groups: existingGroups,
      testimonials: event.testimonials || [], topics: event.topics || [],
      google_maps_embed: event.google_maps_embed || '', directions: event.directions || [],
      content_sections: event.content_sections || [], content_section_title: event.content_section_title || '',
      content_blocks: event.content_blocks || [], seo_keywords: event.seo_keywords || [],
      rss_feed_url: event.rss_feed_url || '', rss_feed_title: event.rss_feed_title || '',
      header_banner_enabled: event.header_banner_enabled || false,
      header_banner_text: event.header_banner_text || '',
      header_banner_image: event.header_banner_image || '',
    });
    setDialogOpen(true);
  };

  const openNewEventDialog = () => { setEditingEvent(null); setFormData({ ...initialFormData }); setDialogOpen(true); };

  /* ---------- Array helpers ---------- */
  const addStat = () => setFormData({ ...formData, stats: [...formData.stats, { icon: 'default', value: '', label: '' }] });
  const updateStat = (i, f, v) => { const s = [...formData.stats]; s[i][f] = v; setFormData({ ...formData, stats: s }); };
  const removeStat = (i) => setFormData({ ...formData, stats: formData.stats.filter((_, x) => x !== i) });

  const addDirection = () => setFormData({ ...formData, directions: [...formData.directions, { icon: 'default', title: '', description: '' }] });
  const updateDirection = (i, f, v) => { const d = [...formData.directions]; d[i][f] = v; setFormData({ ...formData, directions: d }); };
  const removeDirection = (i) => setFormData({ ...formData, directions: formData.directions.filter((_, x) => x !== i) });

  /* ---------- Sponsor groups helpers (nested: groups -> items) ---------- */
  const genGroupId = () => `grp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const addSponsorGroup = () => setFormData({
    ...formData,
    sponsor_groups: [...(formData.sponsor_groups || []), { id: genGroupId(), name: 'Nuova Tipologia', items: [] }]
  });
  const updateSponsorGroup = (groupIdx, field, value) => {
    const groups = [...(formData.sponsor_groups || [])];
    if (!groups[groupIdx]) return;
    groups[groupIdx] = { ...groups[groupIdx], [field]: value };
    setFormData({ ...formData, sponsor_groups: groups });
  };
  const removeSponsorGroup = (groupIdx) => setFormData({
    ...formData,
    sponsor_groups: (formData.sponsor_groups || []).filter((_, x) => x !== groupIdx)
  });
  const reorderSponsorGroups = (oldIdx, newIdx) => {
    const groups = [...(formData.sponsor_groups || [])];
    const [m] = groups.splice(oldIdx, 1);
    groups.splice(newIdx, 0, m);
    setFormData({ ...formData, sponsor_groups: groups });
  };
  const addSponsorItem = (groupIdx) => {
    const groups = [...(formData.sponsor_groups || [])];
    if (!groups[groupIdx]) return;
    groups[groupIdx] = { ...groups[groupIdx], items: [...(groups[groupIdx].items || []), { name: '', logo: '', url: '' }] };
    setFormData({ ...formData, sponsor_groups: groups });
  };
  const updateSponsorItem = (groupIdx, itemIdx, field, value) => {
    const groups = [...(formData.sponsor_groups || [])];
    if (!groups[groupIdx]) return;
    const items = [...(groups[groupIdx].items || [])];
    items[itemIdx] = { ...items[itemIdx], [field]: value };
    groups[groupIdx] = { ...groups[groupIdx], items };
    setFormData({ ...formData, sponsor_groups: groups });
  };
  const removeSponsorItem = (groupIdx, itemIdx) => {
    const groups = [...(formData.sponsor_groups || [])];
    if (!groups[groupIdx]) return;
    groups[groupIdx] = { ...groups[groupIdx], items: (groups[groupIdx].items || []).filter((_, x) => x !== itemIdx) };
    setFormData({ ...formData, sponsor_groups: groups });
  };
  const reorderSponsorItems = (groupIdx, oldIdx, newIdx) => {
    const groups = [...(formData.sponsor_groups || [])];
    if (!groups[groupIdx]) return;
    const items = [...(groups[groupIdx].items || [])];
    const [m] = items.splice(oldIdx, 1);
    items.splice(newIdx, 0, m);
    groups[groupIdx] = { ...groups[groupIdx], items };
    setFormData({ ...formData, sponsor_groups: groups });
  };

  const addTestimonial = () => setFormData({ ...formData, testimonials: [...formData.testimonials, { quote: '', author: '', role: '', company: '', image: '' }] });
  const updateTestimonial = (i, f, v) => { const t = [...formData.testimonials]; t[i][f] = v; setFormData({ ...formData, testimonials: t }); };
  const removeTestimonial = (i) => setFormData({ ...formData, testimonials: formData.testimonials.filter((_, x) => x !== i) });

  const addTopic = () => { if (topicInput.trim() && !formData.topics.includes(topicInput.trim())) { setFormData({ ...formData, topics: [...formData.topics, topicInput.trim()] }); setTopicInput(''); } };
  const removeTopic = (i) => setFormData({ ...formData, topics: formData.topics.filter((_, x) => x !== i) });

  const addSeoKeyword = () => { if (seoKeywordInput.trim()) { setFormData({ ...formData, seo_keywords: [...(formData.seo_keywords || []), seoKeywordInput.trim()] }); setSeoKeywordInput(''); } };
  const removeSeoKeyword = (i) => setFormData({ ...formData, seo_keywords: (formData.seo_keywords || []).filter((_, x) => x !== i) });

  /* ---------- CRUD ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Keep legacy flat `sponsors` in sync with nested `sponsor_groups` for backward compat
      const syncedSponsors = flattenSponsorGroups(formData.sponsor_groups || []);
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title),
        sponsors: syncedSponsors,
      };
      if (editingEvent) { await eventsAPI.update(editingEvent.id, data); toast.success('Evento aggiornato'); }
      else { await eventsAPI.create(data); toast.success('Evento creato'); }
      setDialogOpen(false); resetForm(); loadData();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try { await eventsAPI.delete(id); toast.success('Evento eliminato'); loadData(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const getEditionTitle = (eid) => editions.find(e => e.id === eid)?.title || '-';

  /* ---------- Analytics ---------- */
  const loadEventAnalytics = async (eventId) => {
    if (!eventId) return;
    setAnalyticsLoading(true);
    try { const res = await api.get(`/analytics/event/${eventId}`); setEventAnalytics(res.data); }
    catch { toast.error('Errore nel caricamento delle analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const resetEventAnalytics = async (eventId) => {
    if (!eventId || !window.confirm('Sei sicuro di voler azzerare le statistiche di questo evento?')) return;
    try { await api.delete(`/analytics/event/${eventId}`); setEventAnalytics({ event_id: eventId, total_clicks: 0, stats_by_type: [] }); toast.success('Statistiche azzerate'); }
    catch { toast.error("Errore nell'azzeramento delle statistiche"); }
  };

  /* ==================== RENDER ==================== */
  return (
    <div className="space-y-8" data-testid="events-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Eventi</h1>
          <p className="text-muted-foreground mt-1">Gestisci gli eventi SIOS</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-event-btn" onClick={openNewEventDialog}>
              <Plus size={18} className="mr-2" /> Nuovo Evento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-xl">
                  {editingEvent ? 'Modifica Evento' : 'Nuovo Evento'}
                </DialogTitle>
                {editingEvent?.slug && (
                  <a
                    href={editingEvent.status === 'draft' && editingEvent.preview_token
                      ? `/events/${editingEvent.slug}?preview=${editingEvent.preview_token}`
                      : `/events/${editingEvent.slug}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                  >
                    <Eye size={14} /> Anteprima <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid grid-cols-9 mb-6">
                  <TabsTrigger value="basic">Base</TabsTrigger>
                  <TabsTrigger value="media">Media</TabsTrigger>
                  <TabsTrigger value="tickets">Biglietti</TabsTrigger>
                  <TabsTrigger value="content">Contenuti</TabsTrigger>
                  <TabsTrigger value="agenda" disabled={!editingEvent}>Agenda</TabsTrigger>
                  <TabsTrigger value="blocks">Blocchi</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="seo">SEO</TabsTrigger>
                  <TabsTrigger value="analytics" disabled={!editingEvent}>Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <BasicInfoTab formData={formData} setFormData={setFormData} editions={editions}
                    topicInput={topicInput} setTopicInput={setTopicInput}
                    addStat={addStat} updateStat={updateStat} removeStat={removeStat}
                    addTopic={addTopic} removeTopic={removeTopic} />
                </TabsContent>

                <TabsContent value="media" className="space-y-4">
                  <MediaTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="tickets" className="space-y-4">
                  <TicketsTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="content" className="space-y-6">
                  <ContentTab formData={formData} setFormData={setFormData}
                    addSponsorGroup={addSponsorGroup}
                    updateSponsorGroup={updateSponsorGroup}
                    removeSponsorGroup={removeSponsorGroup}
                    reorderSponsorGroups={reorderSponsorGroups}
                    addSponsorItem={addSponsorItem}
                    updateSponsorItem={updateSponsorItem}
                    removeSponsorItem={removeSponsorItem}
                    reorderSponsorItems={reorderSponsorItems}
                    addTestimonial={addTestimonial} updateTestimonial={updateTestimonial} removeTestimonial={removeTestimonial} />
                </TabsContent>

                <TabsContent value="agenda" className="space-y-6">
                  <AgendaTab editingEvent={editingEvent} sessions={sessions} setSessions={setSessions}
                    tracks={tracks} speakers={speakers} />
                </TabsContent>

                <TabsContent value="blocks" className="space-y-6">
                  <BlocksTab
                    formData={formData}
                    setFormData={setFormData}
                    publicUrl={formData.slug ? `${window.location.origin}/events/${formData.slug}` : null}
                  />
                </TabsContent>

                <TabsContent value="location" className="space-y-4">
                  <LocationTab formData={formData} setFormData={setFormData}
                    addDirection={addDirection} updateDirection={updateDirection} removeDirection={removeDirection} />
                </TabsContent>

                <TabsContent value="seo" className="space-y-4">
                  <SeoTab formData={formData} setFormData={setFormData}
                    seoKeywordInput={seoKeywordInput} setSeoKeywordInput={setSeoKeywordInput}
                    addSeoKeyword={addSeoKeyword} removeSeoKeyword={removeSeoKeyword} />
                </TabsContent>

                <TabsContent value="analytics" className="space-y-6">
                  <AnalyticsTab editingEvent={editingEvent} eventAnalytics={eventAnalytics}
                    analyticsLoading={analyticsLoading} loadEventAnalytics={loadEventAnalytics}
                    resetEventAnalytics={resetEventAnalytics} />
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="save-event-btn">{editingEvent ? 'Salva Modifiche' : 'Crea Evento'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Titolo</TableHead>
              <TableHead>Edizione</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id} className="border-white/5" data-testid={`event-row-${event.id}`}>
                <TableCell className="font-medium">{event.title}</TableCell>
                <TableCell>{getEditionTitle(event.edition_id)}</TableCell>
                <TableCell>{event.date || '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1"><MapPin size={12} className="text-muted-foreground" />{event.location}</div>
                </TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-1 rounded-full ${event.status === 'publish' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                    {event.status === 'publish' ? 'Pubblicato' : 'Bozza'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" asChild title="Anteprima">
                      <a href={event.status === 'draft' && event.preview_token ? `/events/${event.slug}?preview=${event.preview_token}` : `/events/${event.slug}`}
                        target="_blank" rel="noopener noreferrer" data-testid={`preview-event-${event.id}`}>
                        <Eye size={16} className="text-blue-400" />
                      </a>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(event)} data-testid={`edit-event-${event.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(event.id)} data-testid={`delete-event-${event.id}`}><Trash2 size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <MapPin className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">Nessun evento trovato</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
